import { ErrorMessage } from '@hookform/error-message'
import { zodResolver } from '@hookform/resolvers/zod'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { type TFunction } from 'i18next'
import React from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as XLSX from 'xlsx'
import { z } from 'zod'

import { Typography } from '~/components/elements/Typography'
import { useFormField } from '~/hooks/useFormField'
import { exists } from '~/utils'

import { createFileInfoSchema } from '../utils'

import {
  UploadFileSection,
  type UploadFileSectionProps,
} from './UploadFileSection'

const createSchema = (
  fileExtension: string | undefined,
  selectSheetNameMessage: string,
  t: TFunction<'translation', undefined>,
) => {
  if (fileExtension === '.xlsx') {
    return createFileInfoSchema(t).extend({
      sheetName: z.string({ required_error: selectSheetNameMessage }),
    })
  }

  return createFileInfoSchema(t).extend({
    sheetName: z.undefined(),
  })
}
type InputValues = z.infer<ReturnType<typeof createSchema>>

export type UploadAnswerFileSectionProps = {
  onSubmit: (data: InputValues) => void
}

export const UploadAnswerFileSection = ({
  onSubmit,
}: UploadAnswerFileSectionProps) => {
  const { t } = useTranslation()
  const [fileExtension, setFileExtension] = React.useState<string | undefined>(
    undefined,
  )
  const selectSheetNameMessage = t('please_select_sheet_name')
  const selectCorrectSheetNameMessage = t(
    'please_select_the_correct_sheet_name',
  )
  const sheetNameLabel = t('sheet_name')

  const resolver = (
    fileExtension: string | undefined,
    selectSheetNameMessage: string,
  ) => zodResolver(createSchema(fileExtension, selectSheetNameMessage, t))

  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm<InputValues>({
    resolver: resolver(fileExtension, selectSheetNameMessage),
  })
  const sheetNameField = useFormField(control, 'sheetName')

  const [sheetNames, setSheetNames] = React.useState<string[] | undefined>(
    undefined,
  )
  const handleFileInfoChange = React.useCallback<
    EH<UploadFileSectionProps<InputValues>, 'onFileInfoChange'>
  >(
    async (fileInfo) => {
      setFileExtension(fileInfo?.extension)

      const updateSheetNames = (sheetNames: string[] | undefined) => {
        setSheetNames(sheetNames)
        sheetNameField.onChange(sheetNames?.[0])
      }

      if (fileInfo?.extension !== '.xlsx') {
        updateSheetNames(undefined)

        return
      }
      const file = fileInfo.getRawFile?.()
      if (!exists(file)) {
        updateSheetNames(undefined)

        return
      }
      const sheetNames = await new Promise<string[]>((resolve) => {
        const reader = new FileReader()

        reader.onload = (event) => {
          const workBook = XLSX.read(event.target?.result, { type: 'array' })
          resolve(workBook.SheetNames)
        }

        reader.readAsArrayBuffer(file)
      })
      updateSheetNames(sheetNames)
    },
    [sheetNameField],
  )

  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    (data) => {
      if (data.sheetName && !sheetNames?.includes(data.sheetName)) {
        setError('sheetName', {
          type: 'custom',
          message: selectCorrectSheetNameMessage,
        })

        return
      }

      onSubmit(data)
    },
    [onSubmit, setError, sheetNames, selectCorrectSheetNameMessage],
  )

  return (
    <UploadFileSection
      control={control}
      extraFormLabel={fileExtension === '.xlsx' ? sheetNameLabel : undefined}
      onFileInfoChange={handleFileInfoChange}
      onUpload={handleSubmit(handleValid)}
    >
      {sheetNames && (
        <DropDownList
          data={sheetNames}
          disabled={!exists(sheetNames)}
          {...sheetNameField}
        />
      )}

      <ErrorMessage
        errors={errors}
        name="sheetName"
        render={({ message }) => (
          <Typography color="error" variant="normal">
            {message}
          </Typography>
        )}
      />
    </UploadFileSection>
  )
}
